/**
 * MockActivityBridge — a working ActivityBridge for a plain browser: no Discord iframe, no SDK,
 * no OAuth. Activates on `?mock=1`. Query params (DESIGN-client §11):
 *
 *   user=<id>            stable user id (default random per tab)
 *   name=<display name>  default "Mock Player"
 *   instance=<id>        Activity instance id → one table per instance (4 tabs = 4 players)
 *   platform=desktop|mobile
 *   layout=focused|pip|grid
 *   speak=<userId>       toggles that user's speaking flag every 2s (ducking tests)
 *   participants=<n>     fabricate n extra "in call, not seated" participants
 */
import {
  type ActivityBridge,
  type ActivityParticipant,
  type ActivityPlatform,
  type LayoutMode,
  type Orientation,
  type VoiceUpdate,
  vibrate,
  watchOrientation,
} from './ActivityBridge';

export interface MockParams {
  userId: string;
  name: string;
  instanceId: string;
  channelId: string;
  platform: ActivityPlatform;
  layout: LayoutMode;
  speak: string | null;
  participants: number;
}

const FAKE_NAMES = ['Wren', 'Silas', 'Juno', 'Tamsin', 'Otis', 'Mara', 'Reed', 'Ines'];
export const SPEAK_TOGGLE_MS = 2000;

/** True when the app should use the mock bridge (`?mock=1` or bare `?mock`). */
export function isMockRequested(search: string = globalThis.location?.search ?? ''): boolean {
  const params = new URLSearchParams(search);
  return params.get('mock') === '1' || params.has('mock');
}

/** Pure parser (tested). `rand` seeds the per-tab defaults. */
export function parseMockParams(search: string, rand: string = Math.random().toString(36).slice(2, 10)): MockParams {
  const p = new URLSearchParams(search);
  const num = (v: string | null, d: number) => {
    const n = v == null ? NaN : Number(v);
    return Number.isFinite(n) && n >= 0 ? Math.floor(n) : d;
  };
  const layout = p.get('layout');
  return {
    userId: p.get('user') ?? `mock-user-${rand}`,
    name: p.get('name') ?? 'Mock Player',
    instanceId: p.get('instance') ?? `mock-instance-${rand}`,
    channelId: p.get('channel') ?? `mock-channel-${rand}`,
    platform: p.get('platform') === 'mobile' ? 'mobile' : 'desktop',
    layout: layout === 'pip' || layout === 'grid' ? layout : 'focused',
    speak: p.get('speak'),
    participants: num(p.get('participants'), 0),
  };
}

const TAB_RAND = Math.random().toString(36).slice(2, 10);

export class MockActivityBridge implements ActivityBridge {
  readonly kind = 'mock' as const;
  readonly params: MockParams;
  private layoutListeners = new Set<(m: LayoutMode) => void>();
  private layoutMode: LayoutMode;

  constructor(params?: Partial<MockParams>, search: string = globalThis.location?.search ?? '') {
    this.params = { ...parseMockParams(search, TAB_RAND), ...params };
    this.layoutMode = this.params.layout;
  }

  async ready(): Promise<void> {}

  async authorize(): Promise<{ code: string }> {
    return { code: `mock-code-${this.params.userId}` };
  }

  async authenticate(): Promise<void> {}

  instanceId(): string {
    return this.params.instanceId;
  }

  channelId(): string | null {
    return this.params.channelId;
  }

  guildId(): string | null {
    return null;
  }

  platform(): ActivityPlatform {
    return this.params.platform;
  }

  /** The fabricated roster: me + `participants` extras. */
  participants(): ActivityParticipant[] {
    const me: ActivityParticipant = {
      id: this.params.userId,
      username: this.params.name,
      displayName: this.params.name,
      avatarUrl: null,
    };
    const extras = Array.from({ length: this.params.participants }, (_, i) => {
      const name = FAKE_NAMES[i % FAKE_NAMES.length]! + (i >= FAKE_NAMES.length ? String(i) : '');
      return { id: `mock-p${i + 1}`, username: name, displayName: name, avatarUrl: null };
    });
    return [me, ...extras];
  }

  subscribeParticipants(cb: (participants: ActivityParticipant[]) => void): () => void {
    const t = setTimeout(() => cb(this.participants()), 0);
    return () => clearTimeout(t);
  }

  voiceAvailable(): boolean {
    return true;
  }

  subscribeVoice(cb: (u: VoiceUpdate) => void): () => void {
    const who = this.params.speak;
    if (!who) return () => {};
    let speaking = true;
    cb({ userId: who, speaking });
    const iv = setInterval(() => {
      speaking = !speaking;
      cb({ userId: who, speaking });
    }, SPEAK_TOGGLE_MS);
    return () => clearInterval(iv);
  }

  inviteAvailable(): boolean {
    return false;
  }

  async openInvite(): Promise<void> {
    throw new Error('mock bridge: no invite dialog');
  }

  async openExternalLink(url: string): Promise<void> {
    globalThis.window?.open(url, '_blank', 'noopener');
  }

  /** Test hook: switch layout mode at runtime (e2e drives PIP transitions with this). */
  setLayoutMode(m: LayoutMode): void {
    this.layoutMode = m;
    this.layoutListeners.forEach((l) => l(m));
  }

  subscribeLayoutMode(cb: (m: LayoutMode) => void): () => void {
    this.layoutListeners.add(cb);
    cb(this.layoutMode);
    return () => this.layoutListeners.delete(cb);
  }

  subscribeOrientation(cb: (o: Orientation) => void): () => void {
    return watchOrientation(cb);
  }

  async lockOrientation(): Promise<void> {}

  async encourageHardwareAcceleration(): Promise<void> {}

  haptic(kind: 'light' | 'medium'): void {
    vibrate(kind);
  }

  mockUser(): { id: string; username: string } | null {
    return { id: this.params.userId, username: this.params.name };
  }
}
